import { useState } from "react";
import { Flex, Text, TextField } from "@radix-ui/themes";

type CurrencyInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: string | null;
  min?: number;
  max?: number;
  className?: string;
  inputClassName?: string;
  slotClassName?: string;
  autoFocus?: boolean;
  variant?: "default" | "pill";
};

/**
 * Validates a price string.
 * Returns an error message or null if valid.
 * Rules: must be 0 or >= floor, and >= min.
 * @param floor - minimum non-free price (default $0.50 for Stripe's technical limit)
 */
export function validatePrice(
  value: string,
  min: number = 0,
  floor: number = 0.5,
): string | null {
  if (value === "") return "Price is required";

  const num = parseFloat(value);
  if (isNaN(num) || num < 0) return "Enter a valid amount";

  if (num > 0 && num < floor) {
    return floor >= 1
      ? `$${floor.toFixed(0)} minimum, or $0 for free`
      : `Price must be $0.00 or at least $${floor.toFixed(2)}`;
  }

  if (num < min) return `Minimum price is $${min.toFixed(2)}`;

  return null;
}

export default function CurrencyInput({
  value,
  onChange,
  onBlur,
  placeholder = "0.00",
  error,
  max = 99999,
  className,
  inputClassName,
  slotClassName,
  autoFocus,
  variant = "default",
}: CurrencyInputProps) {
  const isPill = variant === "pill";
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Allow empty
    if (raw === "") {
      onChange("");
      return;
    }

    // Only allow digits and a single decimal point, max 2 decimal places
    if (!/^\d*\.?\d{0,2}$/.test(raw)) {
      return;
    }

    // Prevent values exceeding max
    const num = parseFloat(raw);
    if (!isNaN(num) && num > max) {
      return;
    }

    onChange(raw);
  };

  const handleBlur = () => {
    setIsFocused(false);

    // Format on blur: always show 2 decimal places
    if (value !== "") {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        onChange(num.toFixed(2));
      }
    }

    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <Flex direction="column" className={`gap-1 ${className ?? ""}`}>
      <TextField.Root
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        inputMode="decimal"
        autoFocus={autoFocus}
        data-focused={isFocused || undefined}
        color={error ? "red" : undefined}
        size={isPill ? "2" : undefined}
        style={
          isPill
            ? {
                backgroundColor: "rgba(255,255,255,0.15)",
                boxShadow: "none",
                border: "none",
                borderRadius: "9999px",
                color: "white",
                height: "100%",
              }
            : undefined
        }
        className={
          isPill
            ? `!h-full [&_input]:!text-white [&_input]:!text-[16px] sm:[&_input]:!text-[14px] [&_input]:!font-semibold [&_input]:!bg-transparent [&_input::placeholder]:!text-white/40 ${inputClassName ?? ""}`
            : (inputClassName ?? "")
        }
      >
        <TextField.Slot side="left">
          <Text
            className={
              isPill
                ? `text-[14px] font-semibold text-white/60 ${slotClassName ?? ""}`
                : `text-[14px] text-text-secondary ${slotClassName ?? ""}`
            }
          >
            $
          </Text>
        </TextField.Slot>
      </TextField.Root>
      {error && (
        <Text
          className={`text-[12px] ${isPill ? "text-red-400" : "text-red-500"}`}
        >
          {error}
        </Text>
      )}
    </Flex>
  );
}
